const AUTH_TOKEN_KEY = 'auth_token';
const CLIENT_TYPE_KEY = 'client_type';
const COMPANY_KEY = 'company';
const DEFAULT_CLIENT = 'default';
const CLIENT_TYPE_PATTERN = /^[a-zA-Z0-9_-]+$/;

let cachedClientType = null;
let cachedAuthToken = undefined;

function isValidClientType(value) {
  return typeof value === 'string' && CLIENT_TYPE_PATTERN.test(value);
}

function replaceSearchParam(key) {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has(key)) return;
    urlParams.delete(key);
    const newSearch = urlParams.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  } catch (_) { /* ignore */ }
}

export function resetSessionCache() {
  cachedClientType = null;
  cachedAuthToken = undefined;
}

export function getAuthToken() {
  if (cachedAuthToken !== undefined) {
    return cachedAuthToken;
  }
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('auth_token');
    if (urlToken) {
      cachedAuthToken = urlToken;
      localStorage.setItem(AUTH_TOKEN_KEY, urlToken);
      replaceSearchParam('auth_token');
    } else {
      cachedAuthToken = localStorage.getItem(AUTH_TOKEN_KEY);
    }
  } catch (error) {
    console.warn('Error parsing auth_token:', error);
    cachedAuthToken = null;
  }
  return cachedAuthToken;
}

export function setAuthToken(token) {
  cachedAuthToken = token || null;
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
  else localStorage.removeItem(AUTH_TOKEN_KEY);
}

export function getAuthHeaders(extra = {}) {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };
}

export function getClientType() {
  if (cachedClientType !== null) return cachedClientType;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const clientType = urlParams.get('client_type');
    if (isValidClientType(clientType)) {
      cachedClientType = clientType;
      localStorage.setItem(CLIENT_TYPE_KEY, clientType);
      replaceSearchParam('client_type');
    } else {
      const stored = localStorage.getItem(CLIENT_TYPE_KEY);
      cachedClientType = isValidClientType(stored) ? stored : DEFAULT_CLIENT;
    }
  } catch (error) {
    console.warn('Error parsing client_type:', error);
    cachedClientType = DEFAULT_CLIENT;
  }
  return cachedClientType;
}

export function setClientType(clientType) {
  const next = isValidClientType(clientType) ? clientType : DEFAULT_CLIENT;
  cachedClientType = next;
  localStorage.setItem(CLIENT_TYPE_KEY, next);
  return next;
}

export function resetClientCache() {
  cachedClientType = null;
}

export function isDefaultClient() {
  return getClientType() === DEFAULT_CLIENT;
}

export function getCompany() {
  return localStorage.getItem(COMPANY_KEY) || '';
}

export function setCompany(company) {
  if (company) localStorage.setItem(COMPANY_KEY, company);
  else localStorage.removeItem(COMPANY_KEY);
}

export function captureSessionFromUrl() {
  const token = getAuthToken();
  const clientType = getClientType();
  return { token, clientType, company: getCompany() };
}

export function getClientContext() {
  return {
    token: getAuthToken(),
    clientType: getClientType(),
    company: getCompany(),
  };
}

export function listAllowedClients(user) {
  if (!user) return [];
  const values = [];
  const push = (value) => {
    if (Array.isArray(value)) value.forEach(push);
    else if (isValidClientType(value) && value !== DEFAULT_CLIENT) values.push(value);
  };
  push(user.traffix_client);
  push(user.client_type);
  push(user.allowed_clients);
  return [...new Set(values)];
}

export function resolveAuthorizedClientType(user) {
  const requested = getClientType();
  const allowed = listAllowedClients(user);
  if (allowed.length === 0) return requested;
  if (allowed.includes(requested)) return requested;
  return allowed[0];
}

export function applyAuthorizedClientType(user) {
  const resolved = resolveAuthorizedClientType(user);
  return setClientType(resolved);
}

export async function clearSession({ clearCaches } = {}) {
  setAuthToken(null);
  cachedClientType = null;
  localStorage.removeItem(CLIENT_TYPE_KEY);
  localStorage.removeItem(COMPANY_KEY);
  if (typeof clearCaches === 'function') {
    await clearCaches();
  }
}

export { DEFAULT_CLIENT, AUTH_TOKEN_KEY, CLIENT_TYPE_KEY, COMPANY_KEY };
